// react
import { ReactNode, useContext, useState } from 'react';

// types
import { Act, Pagination } from '../../../../@types';

// router
import { useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextUserProjectPermissions } from '../../../pages/Project/Project.context';

// mui
import { Grid } from '@mui/material';

// cards
import { CardAct } from '../../../cards/CardAct/CardAct';

// ui
import { TableFooter } from '../../../ui/TableFooter/TableFooter';
import { TableHeaders } from '../../../ui/TableHeaders/TableHeaders';
import { TableHeaderProps } from '../../../ui/TableHeaders/TableHeader/TableHeader';

// cards
import { CardEmpty, CardEmptyProps } from '../../../cards/CardEmpty/CardEmpty';

// icons
import { Inventory2Outlined as IconInventory } from '@mui/icons-material';
import { FormSearch } from '../../../forms/FormSearch/FormSearch';
import { BtnFilters } from '../../../ui/BtnFilters/BtnFilters';
import { FilterChangeEvent, FilterProps } from '../../../ui/Filters/Filters';
import { getOptionsActStatuses } from '../../../../utils/options';

// props
export interface TableActsProps {
	acts: Act[];
	isLoading?: boolean;
	pagination: Pagination;
	refetchActs?: () => void;
}

export const TableActs = ({ acts, isLoading, pagination, refetchActs }: TableActsProps) => {
	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// context
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// params
	const paramStatus = searchParams.get('status');

	// defaults
	const defaultFiltersValues = {
		status: [],
	};

	// state
	const [filtersValues, setFiltersValues] = useState({
		status: paramStatus ? paramStatus.split(',') : defaultFiltersValues.status,
	});

	// handlers
	const handleChangeFilterValue = (e: FilterChangeEvent) => {
		const { name, value } = e.target as HTMLInputElement;
		setFiltersValues({
			...filtersValues,
			[name]: value,
		});
	};

	// vars
	const headers: TableHeaderProps[] = [
		{
			GridProps: {
				lg: 3,
			},
			label: 'Name',
			value: 'name',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('duration'),
			value: 'duration',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: 'Created',
			value: 'created',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: 'Updated',
			value: 'modified',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: 'Status',
			value: 'status',
		},
		{
			GridProps: {
				lg: 1,
			},
			label: 'Documents',
			value: 'documents',
		},
		{
			GridProps: {
				lg: true,
			},
			label: 'Payments',
			value: 'payments',
		},
	];

	const idFormFilters = 'form-acts-filters';

	const filters: FilterProps[] = [
		{
			hasAllOption: true,
			hasNoneOption: false,
			hasSearchParam: true,
			id: `${idFormFilters}-status`,
			label: t('status'),
			name: 'status',
			multiple: true,
			onChange: handleChangeFilterValue,
			options: getOptionsActStatuses({ t }),
			type: 'SELECT',
			value: filtersValues.status,
		},
	];

	const CardEmptyProps: CardEmptyProps = {
		empty: {
			actions: [],
			content: { children: t('empty.act.content') },
			IconCircleProps: { icon: (<IconInventory />) as ReactNode },
			title: { children: t('empty.act.title') },
		},
		isLoading,
	};

	if (CardEmptyProps.empty.actions && userProjectPermissions?.act?.create) {
		CardEmptyProps.empty.actions.push({
			children: t('addActivity'),
			onClick: () => {
				searchParams.set('dialog', 'addActivity');
				setSearchParams(searchParams);
			},
			variant: 'contained',
		});
	}

	let content = <CardEmpty {...CardEmptyProps} />;

	if (acts.length) {
		content = (
			<Grid container spacing={2}>
				{acts.map((act) => (
					<Grid item xs={12} key={act.id}>
						<CardAct act={act} refetchActs={refetchActs} />
					</Grid>
				))}
			</Grid>
		);
	}

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Grid container justifyContent="space-between" spacing={2}>
					<Grid item xs={12} md={true}>
						<FormSearch InputProps={{ placeholder: t('searchByIdOrName') }} />
					</Grid>
					<Grid item xs={12} md="auto">
						<BtnFilters
							filters={filters}
							onReset={() => {
								setFiltersValues(defaultFiltersValues);
							}}
						/>
					</Grid>
				</Grid>
			</Grid>
			<Grid item xs={12} sx={{ display: { xs: 'none', lg: 'block' } }}>
				<TableHeaders headers={headers} />
			</Grid>
			<Grid item xs={12}>
				{content}
			</Grid>
			<Grid item xs={12}>
				<TableFooter pagination={pagination} />
			</Grid>
		</Grid>
	);
};
